import { useCallback, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { ScheduleAvailabilityTypes } from '@frontend/api-schedule-availability';
import { useTranslation } from '@frontend/i18n';
import {
  SCHEDULE_AVAILABILITY_HOURS_MAX_WIDTH,
  ScheduleAvailabilityHours,
  ScheduleAvailabilityHoursTimezoneHeader,
  ScheduleAvailabilityHoursValueRef,
  ScheduleAvailabilityUnsavedChangesConfirmation,
} from '@frontend/schedule-availability-hours';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { ButtonBar, Button, useModalControl } from '@frontend/design-system';
import { DirectAvailabilityCheck } from '../../components/DirectAvailabilityCheck';
import { ScheduleBanner } from '../../components/ScheduleBanner';
import { scheduleDirectAvailabilityBannerText } from '../../constants';
import { SETTINGS_TRACKING_IDS } from '../../trackingIds/settings-tracking-ids';

const { officeHoursDirectAvailabilityMessage, officeHoursDirectAvailabilityTitle } =
  scheduleDirectAvailabilityBannerText;

type OfficeHoursSettingsProps = {
  locationId: string;
  officeHoursData?: ScheduleAvailabilityTypes.ScheduleAvailabilityByDayOfWeek;
  onSave: (updatedScheduleAvailability: ScheduleAvailabilityTypes.ScheduleAvailabilityByDayOfWeek) => void;
  isLoading: boolean;
};

export const OfficeHoursSettings = ({
  locationId,
  officeHoursData,

  isLoading,
  onSave,
}: OfficeHoursSettingsProps) => {
  const { t } = useTranslation('schedule');
  const { selectedLocationIds } = useAppScopeStore();
  const isSingleLocation = selectedLocationIds.length === 1;
  const [formState, setFormState] = useState<{ hasError: boolean; hasChanges: boolean }>({
    hasError: false,
    hasChanges: false,
  });

  const availabilityHoursRef = useRef<ScheduleAvailabilityHoursValueRef>();

  const { modalProps: cancelModalProps, triggerProps: cancelTriggerProps } = useModalControl();

  const handleAvailabilityHoursChange = useCallback(
    (hasChanges: boolean, hasError: boolean) => setFormState({ hasChanges, hasError }),
    []
  );

  const handleCancel = (e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e?.stopPropagation();
    if (formState.hasChanges) {
      cancelTriggerProps.onClick();
    }
  };

  const handleDiscardChanges = () => {
    availabilityHoursRef.current?.resetAvailabilityHours();
  };

  const handleSave = () => {
    if (formState.hasError || !formState.hasChanges) {
      return;
    }
    onSave(availabilityHoursRef.current?.getUpdatedScheduleInfo() || {});
  };

  const shouldDisableSave = isLoading || !formState.hasChanges || formState.hasError;

  return (
    <>
      {!isSingleLocation && (
        <DirectAvailabilityCheck locationId={locationId}>
          <ScheduleBanner
            fullWidth
            title={officeHoursDirectAvailabilityTitle}
            message={officeHoursDirectAvailabilityMessage}
            css={{ marginTop: theme.spacing(2) }}
          />
        </DirectAvailabilityCheck>
      )}
      <ScheduleAvailabilityHoursTimezoneHeader locationId={locationId} />
      <ScheduleAvailabilityHours
        isLoading={isLoading}
        scheduleInfo={officeHoursData}
        onChange={handleAvailabilityHoursChange}
        ref={availabilityHoursRef}
      />
      <ButtonBar css={buttonBarStyles}>
        <Button size='large' variant='secondary' onClick={(e) => handleCancel(e)}>
          {t('Cancel')}
        </Button>
        <Button
          size='large'
          trackingId={SETTINGS_TRACKING_IDS.appointmentHours.saveButton}
          disabled={shouldDisableSave}
          onClick={handleSave}
          loading={isLoading}
        >
          {t('Save')}
        </Button>
      </ButtonBar>
      <ScheduleAvailabilityUnsavedChangesConfirmation
        {...cancelModalProps}
        onCancel={cancelModalProps.onClose}
        onSave={handleSave}
        onDiscard={handleDiscardChanges}
      />
    </>
  );
};

const buttonBarStyles = css({
  maxWidth: SCHEDULE_AVAILABILITY_HOURS_MAX_WIDTH,
  borderTop: `thin solid ${theme.colors.neutral20}`,
  borderLeftBottomRadius: theme.borderRadius.small,
  borderRightBottomRadius: theme.borderRadius.small,
  '> button': { width: 'fit-content' },
});
